<script setup lang="ts">
import { computed } from 'vue';
import VSidebar from '@shared/component/VSidebar/VSidebar.vue';
import { isOnderdeelBeschikbaar } from '@auth/marktsegmentBeschikbaarheid';
import { useClaims } from '@auth/useClaims';
import {
  type InstellingenMenuGroup,
  type InstellingenRouteRecord,
} from '@crm-lib/routes/instellingen.routes';
import { faCog } from '@fortawesome/pro-regular-svg-icons';
import { type VSidebarItem } from '@shared/component/VSidebar/utils';
import { useClassicEmbedReset } from '@crm-lib/composables/useClassicEmbedReset';
import { useFeatureToggle } from '@environment/useFeatureToggle';

const props = defineProps<{
  routes: InstellingenRouteRecord[];
}>();

const claims = useClaims();
const instellingenOfferteDefaultsInVueToggle = useFeatureToggle(
  'InstellingenOfferteDefaultsInVueToggle',
);

const groupOrder: InstellingenMenuGroup[] = ['Algemeen', 'Offertes/Aanvragen', 'Risicoscan'];
const { triggerReset } = useClassicEmbedReset();

function onItemClick({ isActive }: { item: VSidebarItem; isActive: boolean }) {
  if (isActive) triggerReset();
}

const menuItems = computed(() => {
  const items = props.routes
    .filter((route) => isOnderdeelBeschikbaar(route.meta.onderdeel, claims.value.marktsegment))
    .filter(
      (route) =>
        !route.isVisible ||
        route.isVisible({
          heeftInstellingenPermissie: claims.value.heeftInstellingenPermissie,
          heeftWebmodulePermissie: claims.value.heeftWebmodulePermissie,
          instellingenOfferteDefaultsInVueToggle:
            instellingenOfferteDefaultsInVueToggle.value ?? false,
        }),
    )
    .map((route) => ({
      label: route.meta.label,
      icon: route.meta.icon,
      route,
      group: route.meta.group,
      indicator: undefined,
    }));

  const grouped = items.reduce(
    (acc, item) => {
      (acc[item.group] ??= []).push(item);
      return acc;
    },
    {} as Record<InstellingenMenuGroup, typeof items>,
  );

  return groupOrder
    .filter((groupName) => grouped[groupName]?.length > 0)
    .map((groupName) => ({
      label: groupName,
      items: grouped[groupName],
    }));
});
</script>
<template>
  <VSidebar
    :items="menuItems"
    :header-icon="faCog"
    header-icon-color="teal"
    header-title="Instellingen"
    :header-loading="false"
    @item-click="onItemClick"
  />
</template>
